// SkillsView — the character-sheet tab, rendered blindly from the vsj.
// Top to bottom: character header (name, THE character level, hp/mana/stamina
// bars), the five STATS as compact colored tiles (their own slow track —
// statvocab colors, visually distinct from skills), skill rows with xp
// progress bars under the served sections, and a collapsible derived-numbers
// reference. Class/goblins live on their own tab now — nothing of them here.
// The only client math is bar fractions from served numbers.

import { clear, el, kbar, tooltip } from '../kit';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';
import { STATS, statColor } from '../statvocab';

const REF_SECTIONS: [string, string][] = [['combat', 'Combat'], ['travel', 'Travel'], ['workmanship', 'Workmanship']];

// served stat ids -> the shared statvocab key (presentation vocabulary only)
const STAT_KEY: Record<string, string> = {
  strength: 'str', dexterity: 'dex', intelligence: 'int', vitality: 'vit', agility: 'agi',
};

const fmt = (n: unknown) => Number(n ?? 0).toLocaleString('en-US');

/** Progress toward next level from the SERVED numbers; a maxed track
 *  (xp_to_next 0) renders full. */
const xpFrac = (s: any): number => {
  const into = Math.max(0, Number(s?.xp_into_level ?? 0));
  const toNext = Math.max(0, Number(s?.xp_to_next ?? 0));
  return toNext <= 0 ? 1 : into / (into + toNext);
};

const xpTip = (name: string, s: any): string => {
  const lvl = Number(s?.level ?? 0);
  const toNext = Number(s?.xp_to_next ?? 0);
  if (toNext <= 0) return `${name} — top level`;
  return `${name} — ${fmt(s?.xp_into_level)} xp into Lv ${lvl}, ${fmt(toNext)} to Lv ${lvl + 1}`;
};

const skillName = (id: string): string => {
  const info = vsj.info('skill_' + id);
  return info?.name ? String(info.name) : id[0].toUpperCase() + id.slice(1);
};

export function skillsView(): { root: HTMLElement; refresh(changed: Set<string>): void } {
  const headBox = el('div', { class: 'sk-head' });
  const statsBox = el('div', { class: 'sk-block' });
  const skillsBox = el('div', { class: 'sk-block' });
  const refBox = el('div', { class: 'sk-block' });
  const root = el('div', { class: 'k-panel k-scroll skills-view' }, headBox, statsBox, skillsBox, refBox);

  // ---- character header: name, level, vitals ----
  const vital = (icon: string, kind: string, label: string, cur: number, max: number) => {
    const bar = kbar(kind, 14);
    bar.set(cur / Math.max(1, max), `${Math.floor(cur)} / ${Math.floor(max)}`);
    const row = el('div', { class: 'sk-vital' }, icons.texture(icon, 18), bar.root);
    tooltip(row, () => label);
    headBox.append(row);
  };

  const refreshCharacter = () => {
    clear(headBox);
    const ch = vsj.at('character') ?? {};
    // `character.level` is THE character level (max of all levels); older
    // servers serve the same number as player_level. Never "overall".
    const lv = ch.level ?? ch.player_level ?? '?';
    headBox.append(
      el(
        'div',
        { class: 'sk-name-row' },
        el('span', { class: 'sk-name', text: String(ch.name ?? '?') }),
        el(
          'div',
          { class: 'sk-lv' },
          el('span', { class: 'sk-lv-cap', text: 'LEVEL' }),
          el('span', { class: 'sk-lv-num', text: String(lv) }),
        ),
      ),
    );
    vital('ui_hp', 'hp', 'Health', Number(ch.hp ?? 0), Number(ch.hp_max ?? 1));
    vital('ui_mana', 'mana', 'Mana', Number(ch.mana ?? 0), Number(ch.mana_max ?? 1));
    vital('ui_stamina', 'stamina', 'Stamina', Number(ch.stamina ?? 0), Number(ch.stamina_max ?? 1));
  };

  // ---- stats: compact colored tiles (the slow track) ----
  const statTile = (id: string, s: any): HTMLElement => {
    const key = STAT_KEY[id] ?? id;
    const abbr = STATS[key]?.abbr ?? key;
    const bar = kbar('xp', 4);
    bar.set(xpFrac(s));
    const tile = el(
      'div',
      { class: 'sk-stat', role: 'button', style: `--tile-c:${statColor(key)}` },
      el('span', { class: 'sk-stat-abbr', text: abbr }),
      el('span', { class: 'sk-stat-lv', text: String(s.level ?? 0) }),
      bar.root,
    );
    tooltip(tile, () => {
      const blurb = vsj.info('skill_' + id)?.trains;
      return xpTip(skillName(id), s) + (blurb ? `\n${blurb}` : '');
    });
    tile.addEventListener('click', () => ui.select('skill_' + id));
    return tile;
  };

  const refreshStats = () => {
    clear(statsBox);
    const st = vsj.at('stats') ?? {};
    const rows = st.rows && typeof st.rows === 'object' ? st.rows : {};
    const order: any[] = Array.isArray(st.order) ? st.order : Object.keys(rows);
    if (!order.length) return;
    statsBox.append(el('div', { class: 'sk-sec', text: 'Stats' }));
    const strip = el('div', { class: 'sk-stats' });
    for (const id of order) {
      const s = rows[String(id)];
      if (s && typeof s === 'object') strip.append(statTile(String(id), s));
    }
    statsBox.append(strip);
  };

  // ---- skills: level badge + name + xp bar rows, served sections ----
  const skillRow = (id: string, s: any): HTMLElement => {
    const name = skillName(id);
    const toNext = Number(s.xp_to_next ?? 0);
    const f = xpFrac(s);
    const bar = kbar('xp', 5);
    bar.set(f);
    if (toNext > 0 && f >= 0.9) bar.root.classList.add('near'); // about to level
    const row = el(
      'div',
      { class: 'k-panel raised sk-row', role: 'button' },
      el('div', { class: 'sk-row-lv', text: String(s.level ?? 0) }),
      el(
        'div',
        { class: 'sk-row-main' },
        el(
          'div',
          { class: 'sk-row-top' },
          el('span', { class: 'sk-row-name', text: name }),
          el('span', { class: 'k-small k-faint sk-row-next', text: toNext > 0 ? `${fmt(toNext)} to next` : 'top' }),
        ),
        bar.root,
      ),
    );
    tooltip(row, () => xpTip(name, s));
    row.addEventListener('click', () => ui.select('skill_' + id));
    return row;
  };

  const refreshSkills = () => {
    clear(skillsBox);
    const sk = vsj.at('skills') ?? {};
    const sections: any[] = Array.isArray(sk.sections) ? sk.sections : [];
    const rows = sk.rows && typeof sk.rows === 'object' ? sk.rows : {};
    skillsBox.append(el('div', { class: 'sk-sec', text: 'Skills' }));
    if (!sections.length) {
      skillsBox.append(el('div', { class: 'k-small k-faint', text: 'Skills appear as you use them.' }));
      return;
    }
    for (const sec of sections) {
      if (!sec || typeof sec !== 'object') continue;
      const box = el('div', { class: 'sk-sec-rows' });
      box.append(el('div', { class: 'k-small k-faint sk-sec-name', text: String(sec.name ?? '') }));
      let any = false;
      for (const id of Array.isArray(sec.skills) ? sec.skills : []) {
        const s = rows[String(id)];
        if (s && typeof s === 'object') {
          box.append(skillRow(String(id), s));
          any = true;
        }
      }
      if (any) skillsBox.append(box);
    }
  };

  // ---- reference: derived numbers, collapsible (subordinate material) ----
  let refOpen = sessionStorage.getItem('skills.refOpen') === '1';

  const refRow = (r: any): HTMLElement => {
    const h = el('div', { class: 'sk-ref-row' });
    const label = el('span', { class: 'k-small k-dim sk-ref-label', text: String(r.label ?? '?') });
    if (r.stat != null && STATS[String(r.stat)]) {
      const key = String(r.stat);
      label.prepend(
        el('span', { class: 'sk-ref-abbr', style: `color:${statColor(key)}`, text: STATS[key].abbr }),
      );
    }
    h.append(label);
    const base = r.base ?? 0;
    const withEq = r.with_eq ?? base;
    if (String(base) === String(withEq)) h.append(el('span', { class: 'k-small', text: String(withEq) }));
    else
      h.append(
        el('span', { class: 'k-small k-faint', text: String(base) }),
        el('span', { class: 'k-small k-faint', text: '→' }),
        el('span', { class: 'k-small', text: String(withEq) }),
      );
    if (r.tip) tooltip(h, () => String(r.tip));
    return h;
  };

  const refreshRef = () => {
    clear(refBox);
    const ds = vsj.at('derived_stats') ?? {};
    if (!Object.keys(ds).length) return;
    const head = el(
      'div',
      { class: 'sk-ref-head', role: 'button' },
      el('span', { class: 'sk-ref-chev', text: refOpen ? '▾' : '▸' }),
      el('span', { class: 'sk-sec', text: 'Reference — your derived numbers' }),
    );
    head.addEventListener('click', () => {
      refOpen = !refOpen;
      sessionStorage.setItem('skills.refOpen', refOpen ? '1' : '0');
      refreshRef();
    });
    refBox.append(head);
    if (!refOpen) return;
    const cards = el('div', { class: 'sk-ref-cards' });
    for (const [key, title] of REF_SECTIONS) {
      const page = ds[key];
      if (!page || typeof page !== 'object' || !Object.keys(page).length) continue;
      const card = el('div', { class: 'k-panel quiet sk-ref-card' }, el('div', { class: 'k-small k-dim', text: title }));
      for (const rowId of Object.keys(page)) {
        const r = page[rowId];
        if (r && typeof r === 'object') card.append(refRow(r));
      }
      cards.append(card);
    }
    refBox.append(cards);
  };

  refreshCharacter();
  refreshStats();
  refreshSkills();
  refreshRef();

  return {
    root,
    refresh(changed) {
      const all = changed.has('*');
      if (all || changed.has('character')) refreshCharacter();
      if (all || changed.has('stats') || changed.has('infos')) refreshStats();
      if (all || changed.has('skills') || changed.has('infos')) refreshSkills();
      if (all || changed.has('derived_stats')) refreshRef();
    },
  };
}
